import { css, cx } from '@emotion/css';
import { useNavigator } from '../../routing/navigator';
import { QrIcon } from '../assets/icons';
import { Colors } from '../styles/colors';
import { BOTTOM_NAV_HEIGHT } from '../styles/css-values';
import { labelStyle } from '../styles/text';
import { useLocation } from 'react-router-dom';
import { HeroIcon, IconName } from './HeroIcon';
import { useMemo } from 'react';
import { Motion, Radius, Shadow, unstyledButton } from '../styles/tokens';

type NavigationItem = {
  navigate: () => void;
  icon: IconName;
  label: string;
  path: string;
};

type NavigationItemProps = {
  pathRoot: string;
};

enum NavigationBarLabel {
  HOME = 'home',
  HISTORY = 'history',
  SCAN = 'scan',
  WALLET = 'wallet',
  ACCOUNT = 'account',
}

const NavigationItem = (
  props: NavigationItem & NavigationItemProps,
): JSX.Element => {
  const { path, icon, label, navigate, pathRoot } = props;
  const active = path === pathRoot;
  return (
    <button
      type="button"
      onClick={navigate}
      className={cx(navItemStyle, active && navItemActiveStyle)}
      aria-current={active ? 'page' : undefined}
      aria-label={label}
    >
      <HeroIcon
        outline={!active}
        icon={icon}
        width={24}
        color={active ? Colors.CAPRI_BLUE : Colors.TEXT_SECONDARY}
      />
      <span className={navLabelStyle}>{label}</span>
    </button>
  );
};

const navLabelStyle = css`
  ${labelStyle}
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: inherit;
  margin-top: 4px;
`;

export const BottomNavigationBar = (): JSX.Element => {
  const navigator = useNavigator();
  const location = useLocation();

  const currentPathRoot = useMemo(
    () => location.pathname.slice(1).split('/')[0],
    [location],
  );

  const navigationItems: NavigationItem[] = [
    {
      navigate: () => navigator.toLanding(),
      icon: 'HomeIcon',
      label: NavigationBarLabel.HOME,
      path: NavigationBarLabel.HOME,
    },
    {
      navigate: () => navigator.toHistory(),
      icon: 'ClockIcon',
      label: NavigationBarLabel.HISTORY,
      path: NavigationBarLabel.HISTORY,
    },
    {
      navigate: () => navigator.toScan(),
      icon: 'QrCodeIcon',
      label: NavigationBarLabel.SCAN,
      path: NavigationBarLabel.SCAN,
    },
    {
      navigate: () => navigator.toWallet(),
      icon: 'CreditCardIcon',
      label: NavigationBarLabel.WALLET,
      path: NavigationBarLabel.WALLET,
    },
    {
      navigate: () => navigator.toAccount(),
      icon: 'UserIcon',
      label: NavigationBarLabel.ACCOUNT,
      path: NavigationBarLabel.ACCOUNT,
    },
  ];

  return (
    <nav className={navbarStyle} aria-label="Main">
      {navigationItems.map((item, index) =>
        item.label === 'scan' ? (
          <div key={`item-${index}`} className={qrSlotStyle}>
            <button
              type="button"
              id="qrCode"
              className={qrButtonStyle}
              onClick={item.navigate}
              aria-label="Scan QR code"
            >
              <QrIcon className={qrIconStyle} />
            </button>
          </div>
        ) : (
          <NavigationItem
            key={`item-${index}`}
            {...item}
            pathRoot={currentPathRoot}
          />
        ),
      )}
    </nav>
  );
};

const navbarStyle = css`
  display: grid;
  grid-template-columns: 1fr 1fr 4rem 1fr 1fr;
  position: fixed;
  z-index: 100;
  bottom: 0px;
  left: 0px;
  width: 100%;
  height: ${BOTTOM_NAV_HEIGHT};
  padding-bottom: env(safe-area-inset-bottom, 0px);
  box-sizing: content-box;
  background-color: rgba(255, 255, 255, 0.92);
  -webkit-backdrop-filter: saturate(160%) blur(14px);
  backdrop-filter: saturate(160%) blur(14px);
  border-top: 1px solid ${Colors.BORDER};
  box-shadow: ${Shadow.nav};

  /* On wide screens keep the bar the width of the content column so the
     five targets stay within thumb reach and read as one control. */
  @media (min-width: 48rem) {
    left: 50%;
    transform: translateX(-50%);
    max-width: 30rem;
    border: 1px solid ${Colors.BORDER};
    border-bottom: 0;
    border-top-left-radius: ${Radius.xl};
    border-top-right-radius: ${Radius.xl};
  }
  -webkit-user-select: none; /* Safari */
  -moz-user-select: none; /* Firefox */
  -ms-user-select: none; /* IE10+/Edge */
  user-select: none; /* Standard */
`;

const navItemStyle = css`
  ${unstyledButton}
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: ${BOTTOM_NAV_HEIGHT};
  color: ${Colors.TEXT_SECONDARY};
  transition:
    color ${Motion.fast},
    transform ${Motion.fast};
  -webkit-tap-highlight-color: transparent;

  > svg {
    transition: transform ${Motion.fast};
  }

  &:active > svg {
    transform: scale(0.92);
  }

  &:focus-visible {
    outline: 2px solid ${Colors.CAPRI_BLUE};
    outline-offset: -4px;
    border-radius: ${Radius.md};
  }
`;

const navItemActiveStyle = css`
  color: ${Colors.CAPRI_BLUE};
`;

const qrSlotStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${BOTTOM_NAV_HEIGHT};
`;

const qrButtonStyle = css`
  ${unstyledButton}
  width: 4rem;
  height: 4rem;
  background-color: ${Colors.CAPRI_BLUE};
  border-radius: ${Radius.md};
  transform: translateY(-32px) rotate(45deg);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${Shadow.brand};
  transition:
    background-color ${Motion.fast},
    transform ${Motion.fast},
    box-shadow ${Motion.fast};

  @media (hover: hover) {
    &:hover {
      background-color: ${Colors.CAPRI_BLUE_DARK};
    }
  }

  &:active {
    transform: translateY(-32px) rotate(45deg) scale(0.95);
  }

  &:focus-visible {
    outline: 2px solid ${Colors.CAPRI_BLUE};
    outline-offset: 3px;
  }
`;

const qrIconStyle = css`
  transform: rotate(-45deg);
`;
